import {
  Box,
  Button,
  HStack,
  IconButton,
  Stack,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  EventDetailScheduledUpdatesSection,
  KYDEventScheduledUpdateSummary,
} from "@common/interfaces/KYDEvent";
import { SectionProps } from "../sections/sectionsComponents";
import { useContext, useState } from "react";
import { useLoading } from "../../hooks/form";
import ShowIfHasPermission from "../../ShowIfHasPermission";
import { FaCalendar, FaPlus } from "react-icons/fa";
import { ScheduledUpdatesTable } from "./ScheduledUpdatesTable";
import EditScheduledUpdateModal from "./EditScheduledUpdateModal";
import DeleteScheduledUpdateDialog, {
  DeleteScheduledUpdateDialogContext,
  withDeleteScheduledUpdateDialog,
} from "./DeleteScheduledUpdateDialog";
import useScheduledUpdates from "./hooks/useScheduledUpdates";
import {
  ScheduledUpdatesContext,
  withScheduledUpdates,
} from "./ScheduledUpdatesContext";
import EventDetailTitle from "../../EventDetailTitle";

const ScheduledUpdatesSection = ({
  data = {
    name: "",
    description: "",
    description_rich: undefined,
    poster: "",
    performers: [],
    ticket_types: [],
    tiered_ticket_types: [],
    currency: undefined,
    id: undefined,
    link: undefined,
    short_link: undefined,
    status: undefined,
    timezone: undefined,
    scheduled_updates: [],
  },
  modalType,
  setModalType,
  permissions,
  isLoading,
  refetchEvent,
}: SectionProps<EventDetailScheduledUpdatesSection["data"]>) => {
  const [loading, updateLoading] = useLoading({ accesscode: false });
  const {
    open: openDeleteScheduledUpdateDialog,
    isOpen: isDeleteScheduledUpdateDialogOpen,
    close: closeDeleteScheduledUpdateDialog,
  } = useContext(DeleteScheduledUpdateDialogContext);
  const { open: openScheduledUpdates, close: closeScheduledUpdates } =
    useContext(ScheduledUpdatesContext);
  const {
    actions,
    state: { loading: scheduledUpdateLoading },
  } = useScheduledUpdates();

  const hasPermission = (permissionToCheck) =>
    permissions.includes(permissionToCheck);

  if (!data.scheduled_updates?.length) {
    return null;
  }

  return (
    <Stack>
      <DeleteScheduledUpdateDialog
        isLoading={scheduledUpdateLoading.scheduledUpdate}
      />
      <Stack
        justify={"space-between"}
        w="100%"
        direction={["column", "row"]}
        alignItems={["flex-start", "center"]}
      >
        <EventDetailTitle>Scheduled Updates</EventDetailTitle>
        <ShowIfHasPermission permissions={permissions} checkFor="write">
          {data.scheduled_updates?.length ? (
            <Button
              aria-label="Edit scheduled updates"
              variant={"outline"}
              leftIcon={<FaCalendar />}
              onClick={() => {
                openScheduledUpdates({
                  scheduledUpdate: data.scheduled_updates[0],
                  field: "performers",
                });
              }}
            >
              Edit scheduled updates
            </Button>
          ) : (
            <IconButton
              isDisabled={isLoading}
              variant={"outline"}
              aria-label="Add scheduled update"
              icon={<FaPlus />}
              onClick={() => {
                openScheduledUpdates({ field: "performers" });
              }}
            />
          )}
        </ShowIfHasPermission>
      </Stack>
      <ScheduledUpdatesTable
        scheduledUpdates={data.scheduled_updates}
        onEdit={(update, field) => {
          openScheduledUpdates({ scheduledUpdate: update, field });
        }}
        onDelete={(update) => {
          openDeleteScheduledUpdateDialog({
            title: "Delete scheduled update",
            text: `Are you sure you want to delete this scheduled update?`,
            actions: [
              {
                text: "Delete",
                type: "danger",
                buttonProps: {
                  bg: "red.500",
                  color: "white",
                },
                onClick: async () => {
                  await actions.onDeleteScheduledUpdate(data.id, update, {
                    onSuccess: async () => {
                      await refetchEvent({ silent: true });
                      closeDeleteScheduledUpdateDialog();
                    },
                  });
                },
              },
              {
                text: "Cancel",
                type: "secondary",
                onClick: closeDeleteScheduledUpdateDialog,
              },
            ],
          });
        }}
        readOnly={!hasPermission("write")}
        timezone={data.timezone}
        isDisabled={isLoading}
        loading={loading}
      />
    </Stack>
  );
};

export default withScheduledUpdates(
  withDeleteScheduledUpdateDialog(ScheduledUpdatesSection)
);
